import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FieldWrap, NativeSelect } from "@/components/Field";
import { AdminRegions } from "@/components/admin/AdminRegions";
import { OrderList } from "@/components/OrderList";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", pastors: "", state: "", city: "", region_id: "", leader_email: "" };

function ChurchDialog({ open, church, onClose, onSaved }) {
  const { regions } = useApp();
  const [v, setV] = useState(EMPTY);
  useEffect(() => { if (open) { setV(church ? { name: church.name, pastors: church.pastors, state: church.state, city: church.city || "", region_id: church.region_id || "", leader_email: church.leader_email || "" } : EMPTY); } }, [open, church]);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!v.name.trim()) { toast.error("Please enter the church name"); return; }
    const body = { ...v, region_id: v.region_id || null };
    try {
      if (church) await api.put(`/churches/${church.id}`, body); else await api.post("/churches", body);
      toast.success("Church saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="church-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{church ? "Edit church" : "Add church"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <FieldWrap label="Church name" required><Input className="h-11" value={v.name} onChange={set("name")} data-testid="church-name-input" /></FieldWrap>
          <FieldWrap label="Pastor name(s)" sub="Separate more than one with a comma"><Input className="h-11" value={v.pastors} onChange={set("pastors")} data-testid="church-pastors-input" /></FieldWrap>
          <FieldWrap label="Church leader email" sub="Optional · receipts for this church are emailed here too"><Input type="email" className="h-11" value={v.leader_email} onChange={set("leader_email")} data-testid="church-leader-email-input" /></FieldWrap>
          <div className="grid grid-cols-2 gap-3">
            <FieldWrap label="City"><Input className="h-11" value={v.city} onChange={set("city")} data-testid="church-city-input" /></FieldWrap>
            <FieldWrap label="State"><Input className="h-11" value={v.state} onChange={set("state")} data-testid="church-state-input" /></FieldWrap>
          </div>
          <FieldWrap label="Region"><NativeSelect value={v.region_id} onChange={(x) => setV({ ...v, region_id: x })} placeholder="Unassigned" options={regions.map((r) => ({ value: r.id, label: r.name }))} testid="church-region-select" /></FieldWrap>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="church-save-btn">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminChurches() {
  const { churches, reload } = useApp();
  const [dialog, setDialog] = useState({ open: false, church: null });
  const saveOrder = async (ids) => {
    try { await api.put("/churches/reorder", { ids }); toast.success("Order saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/churches/${id}`); toast.success("Church deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-on-bg-muted text-sm"><b>{churches.length}</b> churches. Drag the handle or use the arrows to change the order. The order is used everywhere, including reports.</p>
        <Button onClick={() => setDialog({ open: true, church: null })} data-testid="add-church-btn"><Plus className="mr-2 h-4 w-4" /> Add church</Button>
      </div>
      <OrderList items={churches} onSave={saveOrder} testid="church" empty="No churches yet. Tap “Add church” to begin."
        renderItem={(c) => (
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold text-slate-900" data-testid={`church-name-${c.id}`}>{c.name}</div>
              <div className="flex flex-wrap gap-x-4 text-sm text-slate-500">
                {c.pastors && <span>Pastor: {c.pastors}</span>}
                {(c.city || c.state) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[c.city, c.state].filter(Boolean).join(", ")}</span>}
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setDialog({ open: true, church: c })} data-testid={`church-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
            <ConfirmDelete onConfirm={() => remove(c.id)} title={`Delete ${c.name}?`} message="Users of this church will lose access until assigned again. Existing records are kept." testid={`church-delete-${c.id}`} />
          </div>
        )} />
      <ChurchDialog open={dialog.open} church={dialog.church} onClose={() => setDialog({ open: false, church: null })} onSaved={() => { setDialog({ open: false, church: null }); reload(); }} />
      <AdminRegions />
    </div>
  );
}
